import { Combobox } from "@headlessui/react";
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import ChevronDown from "../../Atomos/Icons/ChevronDown";
import Close from "../../Atomos/Icons/Close";
import NewTooltip from "../Tooltip/NewTooltip";
import { useStaffMode } from "../../../hooks/StaffModeProvider";
import { includesFoldedSearch } from "../../../utils/foldSearchText";
import {
  DROPDOWN_MENU_LIST_MAX_HEIGHT_PX,
  DROPDOWN_MENU_SEARCH_BLOCK_HEIGHT_PX,
  resolveDropdownMenuPosition,
  type DropdownMenuPosition,
} from "./dropdownMenuPosition";

interface InputProps {
  title: string;
  data: any[];
  selectedObjst: string | undefined;
  selectedValue?: any;
  compareBy?: string;
  seter: (name: any) => void;
  error?: boolean;
  errormsj?: string;
  tooltip?: string;
  disabled?: boolean;
  required?: boolean;
  getid?: boolean;
  mode?: "member" | "staff";
  name?: string;
  placeholder?: string;
  dynamic?: boolean;
  generalTab?: boolean;
  disableInput?: boolean;
  titleClass?: string;
  ddClass?: string;
  maxHeight?: string;
  internalSearch?: boolean;
  disableSort?: boolean;
  /** When set, used instead of default `name` substring match while searching. */
  filterItem?: (object: any, query: string) => boolean;
  onOpenChange?: (open: boolean) => void;
  /** Paint the menu on document.body so an overflow-hidden card or the page scroller cannot clip it. */
  renderMenuInPortal?: boolean;
}

const DEFAULT_INLINE_PANEL_MAX_HEIGHT_PX = 192;

type MenuFrame = {
  position: DropdownMenuPosition;
  left: number;
  width: number;
  searchBlockHeight: number;
};

function MenuPositionSync({
  anchorRef,
  searchRef,
  preferredPanelHeight,
  internalSearch,
  onFrame,
}: {
  anchorRef: React.RefObject<HTMLDivElement>;
  searchRef: React.RefObject<HTMLDivElement>;
  preferredPanelHeight: number;
  internalSearch: boolean;
  onFrame: (frame: MenuFrame) => void;
}) {
  useLayoutEffect(() => {
    const update = () => {
      const anchor = anchorRef.current;
      if (!anchor) {
        return;
      }

      const rect = anchor.getBoundingClientRect();
      const measuredSearchHeight = searchRef.current?.offsetHeight ?? 0;
      const searchBlockHeight = internalSearch
        ? measuredSearchHeight > 0
          ? measuredSearchHeight
          : DROPDOWN_MENU_SEARCH_BLOCK_HEIGHT_PX
        : 0;

      onFrame({
        position: resolveDropdownMenuPosition({
          triggerTop: rect.top,
          triggerBottom: rect.bottom,
          viewportHeight: window.innerHeight,
          preferredPanelHeight,
        }),
        left: rect.left,
        width: rect.width,
        searchBlockHeight,
      });
    };

    update();
    const scroller = document.getElementById("content-container");
    window.addEventListener("resize", update);
    scroller?.addEventListener("scroll", update);

    return () => {
      window.removeEventListener("resize", update);
      scroller?.removeEventListener("scroll", update);
    };
  }, [anchorRef, internalSearch, onFrame, preferredPanelHeight, searchRef]);

  return null;
}

const OpenStateSync = ({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) => {
  useEffect(() => {
    onOpenChange(open);
  }, [open, onOpenChange]);

  return null;
};

const DropDownList: React.FC<InputProps> = ({
  getid,
  required,
  title,
  data,
  seter,
  selectedObjst,
  selectedValue,
  compareBy,
  error = false,
  errormsj,
  tooltip,
  disabled,
  name,
  placeholder,
  generalTab,
  dynamic,
  disableInput,
  titleClass,
  ddClass,
  maxHeight,
  internalSearch = false,
  disableSort = false,
  filterItem,
  onOpenChange,
  renderMenuInPortal = false,
}) => {
  const [dataQuery, setDataQuery] = useState("");
  const [displayLimit, setDisplayLimit] = useState<number>(100);
  const { isStaffMode } = useStaffMode();
  const color = isStaffMode ? "secondary" : "primary";
  const ITEMS_PER_PAGE = 100;
  const INITIAL_DISPLAY_LIMIT = 100;

  const filteredAndSortedObjects = React.useMemo(() => {
    let filtered = data;

    if (dataQuery !== "") {
      filtered = data?.filter((object) =>
        filterItem
          ? filterItem(object, dataQuery)
          : includesFoldedSearch(String(object.name ?? ""), dataQuery),
      );
    }

    if (!filtered) {
      return [];
    }

    if (disableSort) {
      return [...filtered];
    }

    return [...filtered].sort((a, b) => {
      const nameA = a.name?.toLowerCase() || "";
      const nameB = b.name?.toLowerCase() || "";
      return nameA.localeCompare(nameB);
    });
  }, [data, dataQuery, disableSort, filterItem]);

  const displayedItems = React.useMemo(() => {
    return filteredAndSortedObjects?.slice(0, displayLimit) || [];
  }, [filteredAndSortedObjects, displayLimit]);

  const hasMoreItems = filteredAndSortedObjects.length > displayLimit;

  const handleClearSelection = () => {
    if (!disabled) {
      setDataQuery("");
      setDisplayLimit(INITIAL_DISPLAY_LIMIT);
      if (getid) {
        seter({ id: "", name: "" });
      } else {
        seter(null);
      }
    }
  };

  const inputRef = useRef<HTMLInputElement>(null);
  const anchorRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLDivElement>(null);
  const [inputWidth, setInputWidth] = useState<number>(0);
  const [isOpen, setIsOpen] = useState(false);
  const [menuFrame, setMenuFrame] = useState<MenuFrame | null>(null);
  const preferredPanelHeight = internalSearch
    ? DROPDOWN_MENU_LIST_MAX_HEIGHT_PX + DROPDOWN_MENU_SEARCH_BLOCK_HEIGHT_PX
    : DEFAULT_INLINE_PANEL_MAX_HEIGHT_PX;
  const publishMenuFrame = useCallback((frame: MenuFrame) => {
    setMenuFrame(frame);
  }, []);

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    onOpenChange?.(open);
  };
  const comboboxValue = selectedValue ?? selectedObjst;

  const resolveDisplayValue = (value: any): string => {
    if (value == null || value === "") {
      return "";
    }

    if (typeof value === "string") {
      return value;
    }

    if (
      value &&
      typeof value === "object" &&
      "name" in value &&
      typeof value.name === "string"
    ) {
      return value.name;
    }

    return selectedObjst || "";
  };

  useEffect(() => {
    if (internalSearch && !isOpen) {
      setDataQuery("");
      setDisplayLimit(INITIAL_DISPLAY_LIMIT);
    }
  }, [isOpen, internalSearch]);

  useEffect(() => {
    setDisplayLimit(INITIAL_DISPLAY_LIMIT);
  }, [dataQuery]);

  const handleLoadMore = () => {
    setDisplayLimit((prev) => prev + ITEMS_PER_PAGE);
  };

  useEffect(() => {
    const updateInputWidth = () => {
      if (inputRef.current) {
        setInputWidth(inputRef.current.offsetWidth);
      }
    };

    updateInputWidth();
    window.addEventListener("resize", updateInputWidth);

    return () => {
      window.removeEventListener("resize", updateInputWidth);
    };
  }, []);

  useEffect(() => {
    if (inputWidth < 103) {
      inputRef.current?.setAttribute("placeholder", "");
    } else {
      inputRef.current?.setAttribute(
        "placeholder",
        placeholder ? placeholder : "Select One"
      );
    }
  }, [inputWidth, placeholder]);

  useEffect(() => {
    if (!comboboxValue && inputRef.current) {
      inputRef.current.value = "";
    }
  }, [comboboxValue]);

  useEffect(() => {
    if (selectedObjst && inputRef.current) {
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.scrollLeft = 0;
          inputRef.current.setSelectionRange(0, 0);
        }
      }, 0);
    }
  }, [selectedObjst]);

  return (
    <div
      className={`${ddClass} flex flex-col gap-2 md:gap-[.625rem] h-max w-full ${
        error ? "bottom-0 relative" : ""
      }`}
    >
      {title && (
        <div className="flex gap-2 w-full">
          <label
            className={`${titleClass} text-base md:text-lg font-medium text-gray-font`}
          >
            {title} {required && <span className="text-primary"> *</span>}
          </label>
          {tooltip && <NewTooltip text={tooltip} />}
        </div>
      )}

      <Combobox
        value={comboboxValue}
        onChange={(value) => {
          setDataQuery("");
          setDisplayLimit(INITIAL_DISPLAY_LIMIT);
          seter(value);
        }}
        disabled={disabled}
        by={compareBy}
      >
        {({ open }) => {
          return (
            <div className="relative" ref={anchorRef}>
              <OpenStateSync open={open} onOpenChange={handleOpenChange} />
              <div className={`relative ${error ? "errorInput" : ""}`}>
                <Combobox.Button className="w-full">
                  <div className="flex flex-row relative">
                    <Combobox.Input
                      ref={inputRef}
                      displayValue={resolveDisplayValue}
                      onFocus={() => !internalSearch && setDataQuery("")}
                      autoComplete="new-password"
                      autoCorrect="off"
                      autoCapitalize="none"
                      spellCheck={false}
                      className={`${
                        error
                          ? "!border !border-solid !border-primary text-primary placeholder:!text-primary"
                          : "text-input-gray"
                      } ${ddClass ?? ""} cursor-pointer`}
                      style={
                        selectedObjst
                          ? {
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              textAlign: "left",
                              direction: "ltr",
                            }
                          : undefined
                      }
                      onChange={(event) =>
                        !internalSearch &&
                        setDataQuery(event.currentTarget.value)
                      }
                      name={name ? name : title}
                      readOnly={disableInput || internalSearch}
                    />
                    {selectedObjst && (
                      <div
                        className={`absolute top-1/2 -translate-y-1/2 right-10 h-4 w-4 md:h-5 md:w-5 cursor-pointer z-1 ${
                          disabled
                            ? "fill-gray-300 pointer-events-none"
                            : "fill-gray-font"
                        }`}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleClearSelection();
                        }}
                      >
                        <Close />
                      </div>
                    )}
                    <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                      <div
                        id="chevron-up"
                        className={`${
                          disabled ? "text-gray-200" : "text-gray-font"
                        } w-5 h-5 md:w-6 md:h-6 ${
                          open && "rotate-180 duration-75"
                        } ${disabled ? "fill-gray-300" : "fill-gray-font"}`}
                      >
                        <ChevronDown />
                      </div>
                    </div>
                  </div>
                </Combobox.Button>
              </div>

              {(() => {
                const portaledListMaxHeight =
                  renderMenuInPortal && menuFrame
                    ? Math.max(
                        0,
                        menuFrame.position.maxHeight - menuFrame.searchBlockHeight,
                      )
                    : undefined;
                const optionsPanel = (
              <Combobox.Options
                className={
                  renderMenuInPortal
                    ? `fixed z-[10020] overflow-hidden bg-white rounded-sm shadow-2xl${
                        open ? "" : " hidden"
                      }`
                    : `absolute top-14 w-full ${
                        maxHeight || "max-h-48"
                      } z-30 ${!open && "hidden"} bg-white rounded-sm shadow-2xl`
                }
                style={
                  renderMenuInPortal
                    ? menuFrame
                      ? menuFrame.position.side === "above"
                        ? {
                            bottom: menuFrame.position.bottom,
                            left: menuFrame.left,
                            width: menuFrame.width,
                            maxHeight: menuFrame.position.maxHeight,
                          }
                        : {
                            top: menuFrame.position.top,
                            left: menuFrame.left,
                            width: menuFrame.width,
                            maxHeight: menuFrame.position.maxHeight,
                          }
                      : { visibility: "hidden" }
                    : undefined
                }
              >
                {renderMenuInPortal && open ? (
                  <MenuPositionSync
                    anchorRef={anchorRef}
                    searchRef={searchRef}
                    preferredPanelHeight={preferredPanelHeight}
                    internalSearch={internalSearch}
                    onFrame={publishMenuFrame}
                  />
                ) : null}
                {internalSearch && (
                  <div ref={searchRef} className="sticky top-0 bg-white p-2 z-10">
                    <div className="relative">
                      <input
                        type="text"
                        className="w-full"
                        autoComplete="off"
                        autoCorrect="off"
                        autoCapitalize="none"
                        spellCheck={false}
                        placeholder="Search..."
                        value={dataQuery}
                        onChange={(e) => setDataQuery(e.target.value)}
                        onClick={(e) => e.stopPropagation()}
                      />

                      <div className="absolute inset-y-0 right-2 flex items-center pointer-events-none">
                        <svg
                          className="w-5 h-5 text-[#484F56]"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                          />
                        </svg>
                      </div>
                    </div>
                  </div>
                )}
                <div
                  className={
                    renderMenuInPortal
                      ? "overflow-y-auto"
                      : `${
                          internalSearch ? "max-h-80" : maxHeight || "max-h-48"
                        } overflow-y-auto`
                  }
                  style={
                    portaledListMaxHeight !== undefined
                      ? { maxHeight: portaledListMaxHeight }
                      : undefined
                  }
                >
                  {displayedItems?.map((object: any) => (
                    <Combobox.Option
                      key={object.id}
                      value={
                        getid ? object : object.name ? object.name : "Override"
                      }
                      className={`px-2 md:px-4 py-2 text-base md:text-lg leading-[19px] text-left cursor-pointer ${
                        selectedObjst === object.name
                          ? `bg-${color} !text-white`
                          : "bg-white hover:bg-gray-50"
                      }`}
                    >
                      {object.name ? object.name : "Override"}
                    </Combobox.Option>
                  ))}
                  {hasMoreItems && (
                    <div className="sticky bottom-0 bg-white border-t border-gray-200 p-2 z-10">
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleLoadMore();
                        }}
                        className="w-full text-center text-sm text-primary hover:text-primary-dark font-medium py-1"
                      >
                        Load more (
                        {filteredAndSortedObjects.length - displayLimit}{" "}
                        remaining)
                      </button>
                    </div>
                  )}
                  {filteredAndSortedObjects.length === 0 && (
                    <div className="px-2 md:px-4 py-2 text-base md:text-lg text-gray-500 text-center">
                      No results found
                    </div>
                  )}
                </div>
              </Combobox.Options>
                );
                if (!renderMenuInPortal || typeof document === "undefined") {
                  return optionsPanel;
                }
                return createPortal(optionsPanel, document.body);
              })()}
            </div>
          );
        }}
      </Combobox>
      {error && errormsj && (
        <span className="flex flex-wrap items-center text-base font-medium text-primary">
          {errormsj}
        </span>
      )}
    </div>
  );
};

export default DropDownList;
